import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Clock, Monitor, Check, ShieldCheck, ArrowLeft, ShoppingCart } from "lucide-react";
import api from "../lib/api";
import { formatMXN } from "../lib/format";
import { CATEGORY_LABEL } from "../lib/brand";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then((res) => setProduct(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <p className="text-lg text-slate-500">Producto no encontrado.</p>
        <button onClick={() => navigate("/")} className="mt-4 font-semibold text-brand-forest">Volver al inicio</button>
      </div>
    );

  if (!product) return <div className="py-32 text-center text-slate-400">Cargando…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>

      <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <img src={product.image} alt={product.title} className="h-72 w-full object-cover sm:h-96" />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full brand-navy px-3 py-1 text-xs font-bold uppercase text-white">
              {CATEGORY_LABEL[product.category] || product.category}
            </span>
            {product.ec_code && (
              <span className="font-mono-data rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-brand-forest">
                Estándar {product.ec_code}
              </span>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{product.sector}</span>
          </div>

          <h1 data-testid="product-detail-title" className="mt-4 font-display text-3xl font-extrabold leading-tight text-brand-navy sm:text-4xl">
            {product.title}
          </h1>

          <div className="mt-4 flex flex-wrap gap-5 text-sm font-medium text-slate-600">
            {product.duration && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-brand-forest" /> {product.duration}</span>}
            {product.modality && <span className="flex items-center gap-1.5"><Monitor className="h-4 w-4 text-brand-forest" /> {product.modality}</span>}
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl font-bold text-brand-navy">Descripción</h2>
            <p className="mt-3 leading-relaxed text-slate-600">{product.long_description || product.description}</p>
          </div>

          {product.features?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-brand-navy">Lo que incluye</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-brand-forest" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sticky purchase card */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
            {product.old_price && (
              <span className="text-sm text-slate-400 line-through">{formatMXN(product.old_price)}</span>
            )}
            <div data-testid="product-detail-price" className="font-display text-4xl font-extrabold text-brand-navy">
              {formatMXN(product.price)}
            </div>
            <p className="mt-1 text-xs text-slate-400">Precio en pesos mexicanos · IVA incluido</p>

            <button
              data-testid="product-detail-add-cart-button"
              onClick={() => addItem(product)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl brand-forest py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            >
              <ShoppingCart className="h-4 w-4" /> Agregar al carrito
            </button>
            <button
              onClick={() => { addItem(product); navigate("/carrito"); }}
              className="mt-3 w-full rounded-xl border border-brand-navy py-3.5 text-sm font-bold text-brand-navy transition-colors hover:bg-slate-50"
            >
              Comprar ahora
            </button>

            <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm text-slate-600">
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-forest" /> Certificación con validez oficial</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-forest" /> Pago 100% seguro con PayPal</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-forest" /> Acceso inmediato tras la compra</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
